export const name = 'Auth'

import { useMainStore } from '@/stores/main'
import * as Server from '@/services/Server'
import * as Language from '@/services/Language'
import * as Timezone from '@/services/Timezone'

export async function login(data) {
  const store = useMainStore()
  const path = store.apiPath.login

  const response = await Server.post(path, data)
  console.log("Auth.login response", response.data)
  setToken(response.data.data.access_token)
  setUser(response.data.data.user)
  // What the user is allowed to do comes with the same response (see User::jsPermissions on the
  // server): set here, so no caller of a successful login can forget it.
  setPermissions(response.data.jsPermissions)

  // The language saved in the user settings is the preferred one: apply it before the
  // caller navigates. If the call fails, keep the language already in use.
  await Language.syncFromServer().catch(() => {})

  // Same for the timezone the dates are written in (automatic when the server holds none).
  await Timezone.syncFromServer().catch(() => {})

  return response
}

export async function register(data) {
  const store = useMainStore()
  const path = store.apiPath.register
  return Server.post(path, data)
}

export async function setPermissions(jsPermissions) {
  if (!jsPermissions){
    return
  }
  const store = useMainStore()
  store.setProperty('permissions', jsPermissions.permissions ?? [])
  store.setProperty('roles', jsPermissions.roles ?? [])
}

/**
 * Whether the logged-in user holds a permission ("users.manage") or one of the abilities the
 * server derives from them ("backend.access").
 *
 * This only decides what the client shows: the server refuses the call on its own, so a page is
 * hidden when the user cannot use it — never left reachable because nobody would type the address.
 * Permissions are checked, never roles: changing what a role can do is a server-side change.
 */
export function can(permission) {
  const store = useMainStore()
  return (store.permissions ?? []).includes(permission)
}

/** Whether the user holds at least one of the given permissions. */
export function canAny(permissions) {
  return permissions.some(permission => can(permission))
}

export async function logout(data) {
  const store = useMainStore()
  const path = store.apiPath.logout
  return Server.post(path, data)
    .finally(() => {
      clientLogout()
    })
}

export function clientLogout()
{
  const store = useMainStore()
  revokeToken()
  // Through setProperty like the rest of the state, so the copy kept in local storage goes away
  // with it (see LocalStorageService.saveState).
  store.setProperty('permissions', [])
  store.setProperty('roles', [])
  store.unsetProperty('user', 'auth')
}

export async function forgotPassword(data) {
  const store = useMainStore()
  const path = store.apiPath.forgot_password
  return Server.post(path, data)
}

export async function resetPassword(data) {
  const store = useMainStore()
  const path = store.apiPath.reset_password
  return Server.post(path, data)
}

export function setToken(token) {
  const store = useMainStore()
  store.setProperty('token', token, 'auth')
}

export function revokeToken() {
  const store = useMainStore()
  store.unsetProperty('token', 'auth')
}

export function setUser(user) {
  const store = useMainStore()
  store.setProperty('user', user, 'auth')
}

export function getToken() {
  const store = useMainStore()
  return store.token
}

export function isAuthenticated() {
  const store = useMainStore()
  return store.token != null
}

export function getUser() {
  const store = useMainStore()
  return store.user
}

export function getUserId() {
  const store = useMainStore()
  return store.user?.id
}

export function isVerified() {
  const store = useMainStore()
  return !!store.user?.email_verified_at
}

/**
 * Reload the logged-in user from the server. The copy kept in local storage can be out of date —
 * the account may have been activated in another tab, or after following the link in the email —
 * and the email verification shown by the client comes from that copy. What the user is allowed to
 * do comes back with it, in the same call.
 */
export async function refreshUser() {
  const store = useMainStore()

  if (!isAuthenticated()) {
    return null
  }

  try {
    const response = await Server.get(store.apiPath.user)
    const user = response.data?.data

    if (user) {
      setUser(user)
    }

    setPermissions(response.data?.jsPermissions)

    return user
  } catch (e) {
    // Server.js revokes the token when it is no longer valid: the user stays as it is.
    return null
  }
}

/**
 * Ask for a new activation link for the account email.
 *
 * The address is the one of the logged-in user (the server takes it from the token), so there is
 * nothing to type: the link goes to the address the account already has. Used by the banner that
 * invites to activate the account and by the call to action on the play page.
 */
export async function sendActivationEmail() {
  const store = useMainStore()
  return Server.post(store.apiPath.email_verification_notification)
}

